// region Imports
import React, {useEffect, Suspense} from "react";
import {Fragment, useState} from 'react'
import V56spinner from '../components/loaderIndicators/v56_spinner';
import { Dialog, Menu, Transition } from '@headlessui/react'
import {
    BellIcon,
    DocumentReportIcon,
    MenuAlt1Icon,
    CloudIcon,
    QuestionMarkCircleIcon,
    XIcon, IdentificationIcon,
} from '@heroicons/react/outline';
import BucketIconClean from '../assets/icons/bucketIconClean';
import {
    ChevronDownIcon,
} from '@heroicons/react/solid';
import {
    useRecoilState,
} from 'recoil';
import {
    buckets_,
    loadingText_,
    currPage_, contactInformation, firstName_, lastName_, haveNotifications_, profileComplete_
} from "../App";
import {
    v56a_logout,
    v56a_getContactInfo,
    v56a_getAccount,
    v56a_checkTokenValidity,
    v56a_getContactSetting
} from "../v56_api";
import V56CloudStorage from "./v56CloudStorage";
import V56ProfileList from "../modules/v56_cloud_storage/v56ProfileList";
// endregion

// region Navigation
const navigation = [
    { name: 'Buckets', href: '#', icon: BucketIconClean, current: true },
    { name: 'vDrive', href: '#', icon: CloudIcon, current: false },
    { name: 'vDrive profiles', href: '#', icon: IdentificationIcon, current: false },
    /*{ name: 'Reports', href: '#', icon: DocumentReportIcon, current: false },*/
    /*{ name: 'Users', href: '#', icon: UserGroupIcon, current: false },*/
]
const secondaryNavigation = [
    //{ name: 'Settings', href: '#', icon: CogIcon },
    { name: 'Help', href: 'https://docs.vault56.com', icon: QuestionMarkCircleIcon },
]
// endregion

export default function V56Dashboard(props) {

    // region States
    const [loading, setLoading] = useState(true);
    const [currPage, setCurrPage] = useRecoilState(currPage_);
    const [sidebarOpen, setSidebarOpen] = useState(true);
    const [contactInfo, setContactInfo] = useRecoilState(contactInformation);
    const [firstName, setFirstName] = useRecoilState(firstName_);
    const [lastName, setLastName] = useRecoilState(lastName_);
    const haveNotifications = useRecoilState(haveNotifications_);
    const [profileComplete, setProfileComplete] = useRecoilState(profileComplete_);


    // Personal information
    const [buckets, setBuckets] = useRecoilState(buckets_);
    const [loadingText, setLoadingText] = useRecoilState(loadingText_);

    // endregion

    const V56Reports = React.lazy(() => import('../modules/v56_cloud_storage/reports'));
    const V56Settings = React.lazy(() => import('./v56Settings'));
    const V56vDriveList = React.lazy(() => import('../modules/v56_cloud_storage/v56vDriveList'));


    useEffect(() => {
        (async () => {
            try {

                setLoadingText('Loading contact information...');
                setCurrPage(props.showComp);

                const tokenIsValid = await v56a_checkTokenValidity();
                if (!tokenIsValid) {
                    setLoadingText('Your session has expired. Please log in again.');
                    window.location.href = ('https://secure.vault56.com');
                    return;
                }

                //region Get contact information
                const cInfo = await v56a_getContactInfo();
                const cInfo2 = cInfo.data;
                setFirstName(cInfo2['first_name']);
                setLastName(cInfo2['last_name']);
                //endregion

                // Check if profile information is completed
                setLoadingText('Checking profile information..');
                const profile_complete = await v56a_getContactSetting('v56_profile_complete', cInfo2['id']);
                if (profile_complete.data !== 'true') {
                    window.location.href = ('https://secure.vault56.com');
                    return;
                }

                setProfileComplete(true);

                // If this user belongs to an account, load account information
                if (cInfo2['parent_account'] !== null) {
                    setLoadingText('Loading account information..');
                    const aInfo = await v56a_getAccount(cInfo2['parent_account']);
                }

                setContactInfo(cInfo2);

                setLoading(false);
                setLoadingText('');

            } catch (e) {
                console.log(e);
                window.location.href = 'https://secure.vault56.com';
            }
        })();
    }, [])

    /**
     * Combines multiple class strings to one
     * @param classes
     * @returns {string}
     */
    function classNames(...classes) {
        return classes.filter(Boolean).join(' ')
    }

    /**
     * Renders main content
     * Depends on currPage
     * @type {(function(): (*|null))|*}
     */
    const renderContent = React.useCallback(() => {
        switch(currPage) {
            case 'buckets':
                return <V56CloudStorage/>
            case 'vdrive':
                return <Suspense><V56vDriveList/></Suspense>
            case 'vdrive profiles':
                return <Suspense><V56ProfileList/></Suspense>
            case 'reports':
                return <Suspense><V56Reports/></Suspense>
            case 'settings':
                return <Suspense><V56Settings/></Suspense>

            default:
                return 'There is no page for this module';

        }
    }, [currPage, buckets]);

    /**
     * Renders pulsating notification circle on the bell icon if user has unread notifications
     * Depends on haveNotifications
     * @type {(function(): (*|undefined))|*}
     */
    const renderNotifications = React.useCallback(() => {
        if (haveNotifications === true) {
            return (
                <span className="h-3 w-3">
              <span className="animate-ping absolute inline-flex h-6 w-3 rounded-full bg-[#e67423] opacity-75"></span>
              <span className="relative inline-flex rounded-full h-3 w-3 bg-[#e6581c]"></span>
            </span>
            )
        }
    }, [haveNotifications])

    function handleMenuClick(itemName) {
        setCurrPage(itemName.toLowerCase());
    }

// region Main return
    if (loading) {
        return(<V56spinner/>);
    } else {
            return (
                <>
                    {profileComplete ? (
                    <div className="flex flex-col">
                        <Transition.Root show={sidebarOpen} as={Fragment}>
                            <Dialog as="div" className="fixed inset-0 flex z-40 lg:hidden" onClose={() => {
                                setSidebarOpen(false)
                            }}>
                                <Transition.Child
                                    as={Fragment}
                                    enter="transition-opacity ease-linear duration-300"
                                    enterFrom="opacity-0"
                                    enterTo="opacity-100"
                                    leave="transition-opacity ease-linear duration-300"
                                    leaveFrom="opacity-100"
                                    leaveTo="opacity-0"
                                >
                                    <Dialog.Overlay className="fixed inset-0 bg-gray-600 bg-opacity-75"/>
                                </Transition.Child>
                                <Transition.Child
                                    as={Fragment}
                                    enter="transition ease-in-out duration-300 transform"
                                    enterFrom="-translate-x-full"
                                    enterTo="translate-x-0"
                                    leave="transition ease-in-out duration-300 transform"
                                    leaveFrom="translate-x-0"
                                    leaveTo="-translate-x-full"
                                >
                                    <div className="relative flex-1 flex flex-col max-w-xs w-full pt-5 pb-4 bg-[#5f2881]">
                                        <Transition.Child
                                            as={Fragment}
                                            enter="ease-in-out duration-300"
                                            enterFrom="opacity-0"
                                            enterTo="opacity-100"
                                            leave="ease-in-out duration-300"
                                            leaveFrom="opacity-100"
                                            leaveTo="opacity-0"
                                        >
                                            <div className="absolute top-0 right-0 -mr-12 pt-2">
                                                <button
                                                    type="button"
                                                    className="ml-1 flex items-center justify-center h-10 w-10 rounded-full focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white"
                                                    onClick={() => {
                                                        setSidebarOpen(false)
                                                    }}
                                                >
                                                    <span className="sr-only">Close sidebar</span>
                                                    <XIcon className="h-6 w-6 text-white" aria-hidden="true"/>
                                                </button>
                                            </div>
                                        </Transition.Child>
                                        <div className="flex-shrink-0 flex justify-center px-4 cursor-pointer">
                                            <img
                                                className="h-8 w-auto"
                                                src={require('../assets/img/56_logo_inv.png')}
                                                alt="vault56"
                                                onClick={() => {
                                                    window.location.href = '/dashboard'
                                                }}
                                            />
                                        </div>
                                        <div className="text-gray-400 text-center text-md ml-2">Cloud Storage</div>
                                        <nav
                                            className="mt-5 flex-shrink-0 h-full divide-y divide-[#EEE9F2] overflow-y-auto"
                                            aria-label="Sidebar"
                                        >
                                            <div className="px-2 space-y-1">
                                                {navigation.map((item) => (
                                                    <a
                                                        key={item.name}
                                                        href={item.href}
                                                        onClick={() => handleMenuClick(item.name)}
                                                        className={classNames(
                                                            item.name.toLowerCase() === currPage ? 'border-2 border-[#e6581c] text-[#EEE9F2]' : 'text-[#EEE9F2] hover:text-[#e6581c]',
                                                            'group flex items-center px-2 py-2 text-base font-medium rounded-md'
                                                        )}
                                                        aria-current={item.current ? 'page' : undefined}
                                                    >
                                                        <item.icon className="mr-4 flex-shrink-0 h-6 w-6 text-[#EEE9F2]" aria-hidden="true"/>
                                                        {item.name}
                                                    </a>
                                                ))}
                                            </div>

                                            <div className="mt-6 pt-6">
                                                <div className="px-2 space-y-1">
                                                    {secondaryNavigation.map((item) => (
                                                        <a
                                                            key={item.name}
                                                            href={item.href}
                                                            onClick={() => handleMenuClick(item.name)}
                                                            className={classNames(
                                                                item.name.toLowerCase() === currPage ? 'border-2 border-[#e6581c] text-[#EEE9F2]' : 'text-[#EEE9F2] hover:text-[#e6581c]',
                                                                'group flex items-center px-2 py-2 leading-6 font-medium rounded-md',
                                                            )}
                                                        >
                                                            <item.icon className="mr-4 h-6 w-6 text-[#EEE9F2]" aria-hidden="true"/>
                                                            {item.name}
                                                        </a>
                                                    ))}
                                                </div>
                                            </div>
                                        </nav>
                                    </div>
                                </Transition.Child>
                                <div className="flex-shrink-0 w-14" aria-hidden="true">
                                    {/* Dummy element to force sidebar to shrink to fit close icon */}
                                </div>
                            </Dialog>
                        </Transition.Root>

                        {/* Static sidebar for desktop */}
                        <div className="hidden lg:flex lg:w-64 lg:flex-col lg:fixed lg:inset-y-0">
                            {/* Sidebar component, swap this element with another sidebar if you like */}
                            <div className="flex flex-col flex-grow bg-[#5f2881] pt-5 pb-4 overflow-y-auto">
                                <div className="flex justify-center flex-shrink-0 px-4 cursor-pointer">
                                    <img
                                        className="h-8 w-auto"
                                        src={require('../assets/img/56_logo_inv.png')}
                                        alt="vault56"
                                        onClick={() => {
                                            window.location.href = '/dashboard'
                                        }}
                                    />

                                </div>
                                <div className="text-gray-400 text-center text-md ml-2">Cloud Storage</div>
                                <nav className="mt-5 flex-1 flex flex-col divide-y divide-[#EEE9F2] overflow-y-auto" aria-label="Sidebar">
                                    <div className="px-2 space-y-1">
                                        {navigation.map((item) => (
                                            <a
                                                key={item.name}
                                                href={item.href}
                                                onClick={() => handleMenuClick(item.name)}
                                                className={classNames(
                                                    item.name.toLowerCase() === currPage ? 'border-2 border-[#e6581c] text-[#EEE9F2]' : 'text-[#EEE9F2] hover:text-[#e6581c]',
                                                    'group flex items-center px-2 py-2 text-sm leading-6 font-medium rounded-md'
                                                )}
                                                aria-current={item.current ? 'page' : undefined}
                                            >
                                                <item.icon className="mr-4 flex-shrink-0 h-6 w-6 text-[#EEE9F2]" aria-hidden="true"/>
                                                {item.name}
                                            </a>
                                        ))}
                                    </div>
                                    <div className="mt-6 pt-6">
                                        <div className="px-2 space-y-1">
                                            {secondaryNavigation.map((item) => (
                                                <a
                                                    key={item.name}
                                                    href={item.href}
                                                    onClick={() => handleMenuClick(item.name)}
                                                    className={classNames(
                                                        item.name.toLowerCase() === currPage ? 'border-2 border-[#e6581c] text-[#EEE9F2]' : 'text-[#EEE9F2] hover:text-[#e6581c]',
                                                        'group flex items-center px-2 py-2 text-sm leading-6 font-medium rounded-md',
                                                    )}
                                                >
                                                    <item.icon className="mr-4 h-6 w-6 text-[#EEE9F2]" aria-hidden="true"/>
                                                    {item.name}
                                                </a>
                                            ))}
                                        </div>
                                    </div>
                                </nav>
                            </div>
                        </div>

                        <div className="lg:pl-64 flex flex-col flex-1">
                            <div className="relative z-10 flex h-16 bg-white border-b border-gray-200 lg:border-none">
                                <button
                                    type="button"
                                    className="px-4 border-r border-gray-200 text-gray-400 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-cyan-500 lg:hidden"
                                    onClick={() => {
                                        setSidebarOpen(true)
                                    }}
                                >
                                    <span className="sr-only">Open sidebar</span>
                                    <MenuAlt1Icon className="h-6 w-6" aria-hidden="true"/>
                                </button>
                                {/* Search bar */}
                                <div className="flex justify-end">
                                    <div className="ml-4 flex items-center md:ml-6">
                                        <button
                                            type="button"
                                            className="bg-white p-1 rounded-full text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#5f2881]"
                                        >
                                            <span className="sr-only">View notifications</span>
                                            <BellIcon className="h-6 w-6" aria-hidden="true"/>
                                            {renderNotifications()}
                                        </button>

                                        {/* Profile dropdown */}
                                        <Menu as="div" className="ml-3 relative">
                                            <div>
                                                <Menu.Button className="max-w-xs bg-white rounded-full flex items-center text-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#5f2881] lg:p-2 lg:rounded-md lg:hover:bg-gray-50">
                                                <span
                                                    className="flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-full">
                                                    <span className="text-[#5f2881]">{firstName !== '' ? firstName.slice(0, 1) + lastName.slice(0, 1) : ''}</span>
                                                </span>
                                                    <span className="hidden ml-3 text-gray-700 text-sm font-medium lg:block">
                                                    <span className="sr-only">Open user menu for </span>{contactInfo.account_name === null ? contactInfo.email:contactInfo.account_name}
                                                </span>
                                                    <ChevronDownIcon
                                                        className="hidden flex-shrink-0 ml-1 h-5 w-5 text-gray-400 lg:block"
                                                        aria-hidden="true"
                                                    />
                                                </Menu.Button>
                                            </div>
                                            <Transition
                                                as={Fragment}
                                                enter="transition ease-out duration-100"
                                                enterFrom="transform opacity-0 scale-95"
                                                enterTo="transform opacity-100 scale-100"
                                                leave="transition ease-in duration-75"
                                                leaveFrom="transform opacity-100 scale-100"
                                                leaveTo="transform opacity-0 scale-95"
                                            >
                                                <Menu.Items className="origin-top-right absolute right-0 mt-2 w-48 rounded-md shadow-lg py-1 bg-white ring-1 ring-black ring-opacity-5 focus:outline-none">
                                                    <Menu.Item>
                                                        {({active}) => (
                                                            <a
                                                                href="#"
                                                                onClick={() => handleMenuClick('settings')}
                                                                className={classNames(active ? 'bg-gray-100' : '', 'block px-4 py-2 text-sm text-gray-700')}
                                                            >
                                                                Settings
                                                            </a>
                                                        )}
                                                    </Menu.Item>
                                                    <Menu.Item>
                                                        {({active}) => (
                                                            <a
                                                                href='https://secure.vault56.com'
                                                                onClick={v56a_logout}
                                                                className={classNames(active ? 'bg-gray-100' : '', 'block px-4 py-2 text-sm text-gray-700')}
                                                            >
                                                                Logout
                                                            </a>
                                                        )}
                                                    </Menu.Item>
                                                </Menu.Items>
                                            </Transition>
                                        </Menu>
                                    </div>
                                </div>
                            </div>
                            <main className="pb-8">
                                {/* Page header */}
                                {currPage === 'buckets' ?
                                    <div className="bg-gray-100 shadow">
                                        <div className="px-4 sm:px-6 lg:max-w-6xl lg:mx-auto lg:px-8">
                                            <div className={classNames(
                                                currPage === 'buckets' ? '' : '',
                                                "md:flex md:items-center md:justify-between lg:border-t lg:border-gray-200"
                                            )}>
                                                <div className="flex-1 min-w-0">
                                                    {/* Profile */}
                                                    {currPage === 'buckets' ?
                                                        <div className="flex items-center">
                                                            {/*renderProfileText()*/}
                                                        </div> : ''}
                                                </div>
                                            </div>
                                        </div>
                                    </div> : ''}

                                <div className="mt-8">
                                    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
                                        {renderContent()}
                                    </div>
                                </div>
                            </main>
                        </div>
                    </div>
                        ):''}
                </>
            )

    }
    // endregion
}

